import { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, where, orderBy, onSnapshot, getDocs, documentId, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useClient } from '../contexts/ClientContext';

export interface SavedFilter {
  id?: string;
  name: string;
  description?: string;
  tableName: string;
  filters: { [key: string]: any };
  isPublic: boolean;
  createdBy: string;
  createdByName: string;
  /** Apellido del creador. Poblado en saveFilter o resuelto desde users/{uid} para filtros legacy. */
  createdByLastname?: string;
  /** Email del creador. Poblado en saveFilter o resuelto desde users/{uid} para filtros legacy. */
  createdByEmail?: string;
  clientId?: string; // ID del cliente asociado (opcional para compatibilidad)
  /**
   * PT-#46 — Alcance del filtro según cuántas tomas involucra la sección que lo creó:
   * 'single' = Estadísticas / Publicaciones (una toma), 'multi' = Tendencias (varias tomas).
   * Filtros legacy sin este campo se tratan como 'single' (ver normalizeTomaScope).
   */
  tomaScope?: 'single' | 'multi';
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/** Normaliza el scope de un filtro guardado; legacy (sin campo) → 'single'. */
export const normalizeTomaScope = (f: Pick<SavedFilter, 'tomaScope'>): 'single' | 'multi' =>
  f.tomaScope === 'multi' ? 'multi' : 'single';

// Cache module-level de user lookups para no refetchar entre renders.
const userCache = new Map<string, { name?: string; lastname?: string; email?: string }>();

async function enrichFiltersWithUserInfo(filters: SavedFilter[]): Promise<SavedFilter[]> {
  // uids con info incompleta (lacking lastname o email — legacy)
  const uidsToFetch = new Set<string>();
  for (const f of filters) {
    if (!f.createdBy) continue;
    if ((!f.createdByLastname || !f.createdByEmail) && !userCache.has(f.createdBy)) {
      uidsToFetch.add(f.createdBy);
    }
  }

  if (uidsToFetch.size > 0) {
    // Firestore IN max 30 ids per query. Batchear si más.
    const uidArray = Array.from(uidsToFetch);
    const batches: string[][] = [];
    for (let i = 0; i < uidArray.length; i += 30) batches.push(uidArray.slice(i, i + 30));
    for (const batch of batches) {
      try {
        const usersQuery = query(collection(db, 'users'), where(documentId(), 'in', batch));
        const snapshot = await getDocs(usersQuery);
        for (const docSnap of snapshot.docs) {
          const data = docSnap.data();
          userCache.set(docSnap.id, {
            name: data.name,
            lastname: data.lastname,
            email: data.email,
          });
        }
        // Cache miss: marcar uids no encontrados para no reintentar
        for (const uid of batch) if (!userCache.has(uid)) userCache.set(uid, {});
      } catch (err) {
        console.error('[useSavedFilters] Error fetching users for filter enrichment:', err);
      }
    }
  }

  return filters.map(f => {
    const cached = f.createdBy ? userCache.get(f.createdBy) : null;
    return {
      ...f,
      createdByName: f.createdByName || cached?.name || 'Usuario',
      createdByLastname: f.createdByLastname || cached?.lastname,
      createdByEmail: f.createdByEmail || cached?.email,
    };
  });
}

export interface FilterDifference {
  changed: { [key: string]: { old: any; new: any } };
  added: { [key: string]: any };
  removed: { [key: string]: any };
}

export const useSavedFilters = (tableName?: string, enabled: boolean = true) => {
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser, userData } = useAuth();
  const { selectedClient } = useClient();

  // Cargar filtros guardados
  useEffect(() => {
    // console.log('useSavedFilters - Effect triggered, currentUser:', currentUser?.uid, 'selectedClient:', selectedClient?.clientId, 'enabled:', enabled);
    
    // Si está deshabilitado, no cargar filtros
    if (!enabled) {
      setSavedFilters([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);

    // Si no hay cliente seleccionado, no cargar filtros
    if (!selectedClient) {
      setSavedFilters([]);
      setLoading(false);
      return;
    }

    const filtersRef = collection(db, 'savedFilters');
    let publicFilters: SavedFilter[] = [];
    let privateFilters: SavedFilter[] = [];
    let publicLoaded = false;
    let privateLoaded = false;

    const updateCombinedFilters = async () => {
      if (publicLoaded && (privateLoaded || !currentUser)) {
        // Dedupe por id (un filtro propio público podría matchear ambas queries).
        const byId = new Map<string, SavedFilter>();
        for (const f of [...publicFilters, ...privateFilters]) {
          if (f.id) byId.set(f.id, f);
        }
        const allFilters = Array.from(byId.values()).sort((a, b) => {
          const aTime = a.createdAt?.toMillis() || 0;
          const bTime = b.createdAt?.toMillis() || 0;
          return bTime - aTime;
        });
        // Enriquecer con users/{uid} para mostrar nombre completo + email
        // en filtros legacy (creados antes de guardar estos campos inline).
        const enriched = await enrichFiltersWithUserInfo(allFilters);
        setSavedFilters(enriched);
        setLoading(false);
      }
    };

    // FIX (2026-05-12): split en 2 queries para no violar la rule "list ⊆ rules" de Firestore.
    // La rule allow read es: createdBy==uid OR isPublic==true. Una query SIN where falla
    // entera (permission-denied) si existe AL MENOS UN doc privado de otro usuario.
    // Cada query separada garantiza que TODOS los docs matcheados pasan la rule.
    // Requiere composite indexes (savedFilters: createdBy+clientId+createdAt y isPublic+clientId+createdAt).

    // Query A: filtros públicos del cliente seleccionado (visibles para todos los users).
    const publicQuery = query(
      filtersRef,
      where('isPublic', '==', true),
      where('clientId', '==', selectedClient.clientId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribePublic = onSnapshot(publicQuery, (snapshot) => {
      publicFilters = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as SavedFilter));
      publicLoaded = true;
      updateCombinedFilters();
    }, (err) => {
      console.error('[useSavedFilters] Error loading public filters:', err);
      setError(`Error al cargar filtros públicos: ${err.message}`);
      publicLoaded = true;
      updateCombinedFilters();
    });

    // Query B: filtros privados del usuario actual (cualquier privacy) en el cliente.
    let unsubscribePrivate: (() => void) | null = null;
    if (currentUser) {
      const privateQuery = query(
        filtersRef,
        where('createdBy', '==', currentUser.uid),
        where('clientId', '==', selectedClient.clientId),
        orderBy('createdAt', 'desc')
      );
      unsubscribePrivate = onSnapshot(privateQuery, (snapshot) => {
        privateFilters = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as SavedFilter));
        privateLoaded = true;
        updateCombinedFilters();
      }, (err) => {
        console.error('[useSavedFilters] Error loading private filters:', err);
        setError(`Error al cargar filtros propios: ${err.message}`);
        privateLoaded = true;
        updateCombinedFilters();
      });
    } else {
      privateLoaded = true;
    }

    return () => {
      unsubscribePublic();
      if (unsubscribePrivate) unsubscribePrivate();
    };
  }, [currentUser, selectedClient, enabled]);

  // Guardar nuevo filtro
  const saveFilter = async (
    name: string,
    filters: { [key: string]: any },
    isPublic: boolean = true,
    description?: string,
    filterTableName?: string,
    tomaScope: 'single' | 'multi' = 'single'
  ): Promise<string> => {
    if (!currentUser) {
      throw new Error('Usuario no autenticado');
    }

    if (!userData) {
      throw new Error('Datos de usuario no disponibles');
    }

    if (!selectedClient) {
      throw new Error('No hay cliente seleccionado');
    }

    const filterData: Omit<SavedFilter, 'id'> = {
      name,
      tableName: filterTableName || 'global', // Use 'global' as default if no table specified
      filters,
      isPublic,
      createdBy: currentUser.uid,
      createdByName: userData.name || currentUser.email || 'Usuario',
      // Agregar lastname/email para mostrar atribución completa en UI sin lookup posterior.
      ...(userData.lastname ? { createdByLastname: userData.lastname } : {}),
      ...((userData.email || currentUser.email) ? { createdByEmail: (userData.email || currentUser.email) as string } : {}),
      clientId: selectedClient.clientId, // Asociar al cliente seleccionado
      tomaScope, // PT-#46: 'single' (Stats/Pubs) | 'multi' (Trends)
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      // Solo incluir description si tiene un valor válido
      ...(description && description.trim() ? { description: description.trim() } : {})
    };

    try {
      // console.log('Saving filter with data:', filterData);
      const docRef = await addDoc(collection(db, 'savedFilters'), filterData);
      // console.log('Filter saved successfully with ID:', docRef.id);
      return docRef.id;
    } catch (err) {
      // console.error('Error saving filter:', err);
      throw new Error(`Error al guardar el filtro: ${err instanceof Error ? err.message : 'Error desconocido'}`);
    }
  };

  // Actualizar filtro existente
  const updateFilter = async (
    filterId: string,
    updates: Partial<Pick<SavedFilter, 'name' | 'description' | 'filters' | 'isPublic'>>
  ): Promise<void> => {
    if (!currentUser) {
      throw new Error('Usuario no autenticado');
    }

    const filter = savedFilters.find(f => f.id === filterId);
    if (!filter) {
      throw new Error('Filtro no encontrado');
    }

    if (filter.createdBy !== currentUser.uid) {
      throw new Error('No tienes permisos para modificar este filtro');
    }

    try {
      const filterRef = doc(db, 'savedFilters', filterId);
      
      // Limpiar campos undefined de las actualizaciones
      const cleanUpdates = Object.entries(updates).reduce((acc, [key, value]) => {
        if (key === 'description') {
          // Solo incluir description si tiene un valor válido
          if (value && typeof value === 'string' && value.trim()) {
            acc[key] = value.trim();
          }
        } else if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as any);
      
      await updateDoc(filterRef, {
        ...cleanUpdates,
        updatedAt: Timestamp.now(),
      });
    } catch (err) {
      
      throw new Error('Error al actualizar el filtro');
    }
  };

  // Eliminar filtro
  const deleteFilter = async (filterId: string): Promise<void> => {
    if (!currentUser) {
      throw new Error('Usuario no autenticado');
    }

    const filter = savedFilters.find(f => f.id === filterId);
    if (!filter) {
      throw new Error('Filtro no encontrado');
    }

    if (filter.createdBy !== currentUser.uid) {
      throw new Error('No tienes permisos para eliminar este filtro');
    }

    try {
      await deleteDoc(doc(db, 'savedFilters', filterId));
    } catch (err) {
      
      throw new Error('Error al eliminar el filtro');
    }
  };

  // Comparar filtros para detectar cambios
  const compareFilters = (currentFilters: { [key: string]: any }, savedFilters: { [key: string]: any }): FilterDifference => {
    const difference: FilterDifference = {
      changed: {},
      added: {},
      removed: {}
    };

    // Detectar cambios y nuevos filtros
    Object.keys(currentFilters).forEach(key => {
      if (savedFilters.hasOwnProperty(key)) {
        if (JSON.stringify(currentFilters[key]) !== JSON.stringify(savedFilters[key])) {
          difference.changed[key] = {
            old: savedFilters[key],
            new: currentFilters[key]
          };
        }
      } else {
        difference.added[key] = currentFilters[key];
      }
    });

    // Detectar filtros eliminados
    Object.keys(savedFilters).forEach(key => {
      if (!currentFilters.hasOwnProperty(key)) {
        difference.removed[key] = savedFilters[key];
      }
    });

    return difference;
  };

  // Verificar si hay cambios
  const hasChanges = (currentFilters: { [key: string]: any }, savedFilters: { [key: string]: any }): boolean => {
    const diff = compareFilters(currentFilters, savedFilters);
    return Object.keys(diff.changed).length > 0 || Object.keys(diff.added).length > 0 || Object.keys(diff.removed).length > 0;
  };

  // Buscar filtro que coincida exactamente
  const findMatchingFilter = (currentFilters: { [key: string]: any }): SavedFilter | null => {
    return savedFilters.find(filter => 
      JSON.stringify(filter.filters) === JSON.stringify(currentFilters)
    ) || null;
  };

  return {
    savedFilters,
    loading,
    error,
    saveFilter,
    updateFilter,
    deleteFilter,
    compareFilters,
    hasChanges,
    findMatchingFilter
  };
}; 